import { useCallback, useEffect, useRef } from 'react';

import { BILLING_API } from '@/shared/api/billing';

const INITIAL_DELAY_MS = 2500;
const BACKOFF_FACTOR = 1.5;
const MAX_DELAY_MS = 12_000;
const MAX_CONSECUTIVE_REQUEST_ERRORS = 3;

type UseInvoiceStatusPollingArgs = {
  enabled: boolean;
  invoiceId?: string;
  onError?: (invoiceUrl?: string) => void;
  onFailed: (invoiceUrl?: string) => void;
  onInvoiceUrl?: (invoiceUrl: string) => void;
  onPaid: () => Promise<void> | void;
  onPending?: () => void;
};

export const useInvoiceStatusPolling = ({
  enabled,
  invoiceId,
  onError,
  onFailed,
  onInvoiceUrl,
  onPaid,
  onPending,
}: UseInvoiceStatusPollingArgs): void => {
  const onPaidRef = useRef(onPaid);
  const onFailedRef = useRef(onFailed);
  const onPendingRef = useRef(onPending);
  const onErrorRef = useRef(onError);
  const onInvoiceUrlRef = useRef(onInvoiceUrl);

  useEffect(() => {
    onPaidRef.current = onPaid;
    onFailedRef.current = onFailed;
    onPendingRef.current = onPending;
    onErrorRef.current = onError;
    onInvoiceUrlRef.current = onInvoiceUrl;
  });

  const generationRef = useRef(0);
  const isPausedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scheduledDelayRef = useRef<null | number>(null);
  const scheduleTimerFnRef = useRef<((delay: number) => void) | null>(null);

  const startPoll = useCallback(
    (controller: AbortController, capturedGeneration: number, delayMs: number) => {
      if (!invoiceId) return;

      let consecutiveRequestErrors = 0;

      const poll = async (currentDelay: number) => {
        if (generationRef.current !== capturedGeneration) return;

        let response: Awaited<ReturnType<typeof BILLING_API.getInvoiceStatus>>;
        try {
          response = await BILLING_API.getInvoiceStatus(invoiceId, controller.signal);
        } catch {
          if (generationRef.current !== capturedGeneration) return;
          consecutiveRequestErrors += 1;

          if (consecutiveRequestErrors >= MAX_CONSECUTIVE_REQUEST_ERRORS) {
            onErrorRef.current?.(undefined);
            return;
          }

          // eslint-disable-next-line no-use-before-define
          scheduleNext(currentDelay * BACKOFF_FACTOR);
          return;
        }

        if (generationRef.current !== capturedGeneration) return;
        consecutiveRequestErrors = 0;

        const invoiceUrl = response.invoice_url ?? undefined;
        if (invoiceUrl) onInvoiceUrlRef.current?.(invoiceUrl);

        if (response.status === 'failed') {
          onFailedRef.current(invoiceUrl);
          return;
        }

        if (response.status === 'paid') {
          await onPaidRef.current();
          return;
        }

        onPendingRef.current?.();
        // eslint-disable-next-line no-use-before-define
        scheduleNext(currentDelay * BACKOFF_FACTOR);
      };

      const scheduleNextTimer = (delay: number) => {
        scheduledDelayRef.current = delay;
        timerRef.current = setTimeout(() => {
          scheduledDelayRef.current = null;
          void poll(delay);
        }, delay);
      };

      scheduleTimerFnRef.current = scheduleNextTimer;

      const scheduleNext = (nextDelay: number) => {
        const clampedDelay = Math.min(nextDelay, MAX_DELAY_MS);

        if (isPausedRef.current) {
          scheduledDelayRef.current = clampedDelay;
          return;
        }

        scheduleNextTimer(clampedDelay);
      };

      scheduleNext(delayMs);
    },
    [invoiceId],
  );

  useEffect(() => {
    if (!enabled || !invoiceId) return;

    const controller = new AbortController();
    generationRef.current += 1;
    const capturedGeneration = generationRef.current;

    isPausedRef.current = document.hidden;

    startPoll(controller, capturedGeneration, INITIAL_DELAY_MS);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        isPausedRef.current = true;
        if (timerRef.current !== undefined) {
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
      } else {
        isPausedRef.current = false;
        const hasPendingPoll = scheduledDelayRef.current !== null;
        const scheduleFn = scheduleTimerFnRef.current;
        if (hasPendingPoll && scheduleFn) {
          scheduledDelayRef.current = null;
          scheduleFn(INITIAL_DELAY_MS);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      generationRef.current += 1;

      controller.abort();
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }

      scheduledDelayRef.current = null;
      scheduleTimerFnRef.current = null;
      isPausedRef.current = false;
    };
  }, [enabled, invoiceId, startPoll]);
};
